import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import moment from 'moment';
import { API_ROUTE } from '@constants/index';
import { PlatformCheckerService } from '@services/platform-checker/platform-checker.service';
import { environment } from 'src/environments/environment';
import { DayLog } from '../../models/day-log';
import { YearMonth } from '../../models/year-month';
import { LogsService } from '../../services/logs.service';

@Component({
  selector: 'app-months-list',
  standalone: false,
  templateUrl: './months-list.component.html',
  styleUrls: ['./months-list.component.scss'],
})
export class MonthsListComponent implements OnInit, OnChanges {
  @Input() public yearMonths: YearMonth[] = [];
  @Input() public dayLogs: DayLog[];
  @Input() public monthLogLoading = false;
  @Input() public targetEmployeeId: string;
  @Input() public currentEmployeeId: string;
  @Input() public readOnly = false;
  @Input() public targetHasRemoteWork: boolean;

  public isMobile: boolean;
  public chosenYearMonth: YearMonth;

  public constructor(
    private readonly logsService: LogsService,
    private readonly platformCheckerService: PlatformCheckerService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.isMobile = this.platformCheckerService.isMobile();
  }

  public ngOnInit(): void {
    this.syncYearMonths();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.yearMonths ||
      changes.targetEmployeeId ||
      changes.currentEmployeeId
    ) {
      this.syncYearMonths();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public translateMonthNumber(monthNumber: number): string {
    return moment()
      .month(monthNumber - 1)
      .format('MMMM');
  }

  public toggleChosenYearMonth(yearMonth: YearMonth): void {
    const isSameAsChosen =
      !!this.chosenYearMonth &&
      yearMonth.year === this.chosenYearMonth.year &&
      yearMonth.month === this.chosenYearMonth.month;

    if (!isSameAsChosen) {
      this.monthLogLoading = true;
      this.logsService
        .getYearMonthLogs(
          this.targetEmployeeId,
          yearMonth,
          this.currentEmployeeId
        )
        .subscribe((dayLogs): void => {
          this.dayLogs = dayLogs;
          this.monthLogLoading = false;
          this.cdr.markForCheck();
        });

      this.chosenYearMonth = yearMonth;
      this.persistChosenYearMonth(yearMonth);
    }
  }

  private syncYearMonths(): void {
    this.yearMonths = (this.yearMonths ?? [])
      .slice()
      .sort(YearMonth.sortYearMonths);

    if (!this.yearMonths.length) {
      return;
    }

    if (!this.targetEmployeeId) {
      return;
    }

    if (this.chosenYearMonth) {
      const chosenFromLatestList = this.yearMonths.find(
        (ym: YearMonth): boolean =>
          ym.year === this.chosenYearMonth.year &&
          ym.month === this.chosenYearMonth.month
      );
      if (
        chosenFromLatestList &&
        chosenFromLatestList !== this.chosenYearMonth
      ) {
        this.chosenYearMonth = chosenFromLatestList;
      }
    }

    const restored = this.restoreChosenYearMonth();
    if (restored) {
      const restoredYearMonth = this.yearMonths.find(
        (ym: YearMonth): boolean =>
          ym.year === restored.year && ym.month === restored.month
      );
      const isAlreadyRestored =
        !!this.chosenYearMonth &&
        this.chosenYearMonth.year === restored.year &&
        this.chosenYearMonth.month === restored.month;

      if (restoredYearMonth && !isAlreadyRestored) {
        this.toggleChosenYearMonth(restoredYearMonth);
        return;
      }

      if (restoredYearMonth && this.chosenYearMonth !== restoredYearMonth) {
        this.chosenYearMonth = restoredYearMonth;
      }
    }

    const chosenStillExists = this.chosenYearMonth
      ? this.yearMonths.some(
          (ym: YearMonth): boolean =>
            ym.year === this.chosenYearMonth.year &&
            ym.month === this.chosenYearMonth.month
        )
      : false;

    if (!this.chosenYearMonth || !chosenStillExists) {
      const [chosenYearMonth] = this.yearMonths;
      this.toggleChosenYearMonth(chosenYearMonth);
    }
  }

  private getChosenYearMonthStorageKey(): string {
    const targetEmployeeId = this.targetEmployeeId ?? '';
    const currentEmployeeId = this.currentEmployeeId ?? '';
    return `logs.chosenYearMonth.${targetEmployeeId}.${currentEmployeeId}`;
  }

  private persistChosenYearMonth(yearMonth: YearMonth): void {
    if (!this.targetEmployeeId) {
      return;
    }
    try {
      sessionStorage.setItem(
        this.getChosenYearMonthStorageKey(),
        JSON.stringify({ year: yearMonth.year, month: yearMonth.month })
      );
    } catch {
      // noop
    }
  }

  private restoreChosenYearMonth(): { year: number; month: number } | null {
    try {
      const raw = sessionStorage.getItem(this.getChosenYearMonthStorageKey());
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { year?: number; month?: number };
      if (
        typeof parsed?.year !== 'number' ||
        typeof parsed?.month !== 'number'
      ) {
        return null;
      }
      return { year: parsed.year, month: parsed.month };
    } catch {
      return null;
    }
  }

  public async downloadYearMonth(
    event: Event,
    yearMonth: YearMonth
  ): Promise<void> {
    event.stopPropagation();

    const month = yearMonth.getMonthFormatted();
    const parameter = `?periodo=${month}${yearMonth.year}`;
    const fileUrl =
      ((await this.isInternalNetwork())
        ? API_ROUTE.DOWNLOADHISTORIC.INTERNAL
        : API_ROUTE.DOWNLOADHISTORIC.EXTERNAL) + parameter;
    window.open(fileUrl, '_blank');
  }

  // eslint-disable-next-line class-methods-use-this
  public async isInternalNetwork(): Promise<boolean> {
    return environment.production;
  }

  public shouldShowDownloadMonthLogButton(yearMonth: YearMonth): boolean {
    return (
      yearMonth.isMonthConsolidated &&
      !this.isMobile &&
      this.currentEmployeeId === undefined
    );
  }
}
